import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { CameraAngle, Video } from '../domain/game';
import { AlertService } from '../services/alert.service';
import {
  LiveStreamPlatform,
  Stream,
  VideoService
} from '../services/video.service';

@Component({
  selector: 'app-live-stream-selection',
  templateUrl: './live-stream-selection.component.html',
  styleUrls: ['./live-stream-selection.component.css']
})
export class LiveStreamSelectionComponent {
  @Output() liveStreamAdded = new EventEmitter<Video>();

  mediaServers: string[] = [];
  apps: string[] = [];
  liveStreams: Stream[] = [];

  constructor(
    private videoService: VideoService,
    private alertService: AlertService
  ) {}

  requiresServerSelection(type: string) {
    return type === 'ovenmediaengine';
  }

  async liveStreamTypeChanged(type: LiveStreamPlatform) {
    this.mediaServers = [];
    this.liveStreams = [];
    if (!type) {
      this.liveStreams = [];
      return;
    }
    if (this.requiresServerSelection(type)) {
      this.mediaServers = await firstValueFrom(
        this.videoService.listServers(type)
      );
    } else {
      return this.listLiveStreams(type, null, null);
    }
  }

  serverChanged(type: string, server: string) {
    if (!type) {
      this.liveStreams = [];
      return;
    }
    if (this.requiresServerSelection(type) && !server) {
      this.liveStreams = [];
      return;
    }
    if (type === 'ovenmediaengine') {
      this.apps = ['live'];
    }
  }

  async listLiveStreams(type: string, server: string, app: string) {
    if (!type) {
      this.liveStreams = [];
      return;
    }
    if (this.requiresServerSelection(type) && (!server || !app)) {
      this.liveStreams = [];
      return;
    }

    const mainFirst = (s1: Stream, s2: Stream) => {
      if (s1.camera === CameraAngle.MAIN) {
        return -1;
      }
      if (s2.camera === CameraAngle.MAIN) {
        return 1;
      }
      return s1.camera?.localeCompare(s2.camera ?? '') ?? 0;
    };
    this.liveStreams = await firstValueFrom(
      this.videoService.listStreams(type, server, app)
    );
    this.liveStreams.sort(mainFirst);
  }

  async liveStreamSelected(streamId: string) {
    const liveStream = this.liveStreams.find((s) => s.streamId === streamId);
    if (!liveStream) {
      return;
    }

    if (liveStream.platform === 'mux' && liveStream.status !== 'active') {
      console.log('Mux stream is not active', liveStream.streamId);
      this.alertService.showError(
        `Mux stream ${liveStream.name} is not active. Please start sending video to the endpoint first.`
      );
      return;
    }

    if (liveStream.platform === 'mux' && liveStream.muxData) {
      const assetDetails = await firstValueFrom(
        this.videoService.getMuxAssetDetails(
          liveStream.muxData.streamId,
          liveStream.muxData.activeAssetId
        )
      );
      liveStream.protocols.push({
        type: 'hls',
        url: `https://stream.mux.com/${assetDetails.playback_ids[0].id}.m3u8`
      });
    }

    const cameraAngle = this.mapStreamToCameraAngle(liveStream.camera);
    const videos = this.mapToVideos(liveStream, cameraAngle);
    videos.forEach((v) => {
      this.liveStreamAdded.emit(v);
    });
  }

  private mapToVideos(liveStream: Stream, cameraAngle: CameraAngle) {
    return liveStream.protocols.map(
      (p) =>
        ({
          id: self.crypto.randomUUID(),
          cameraAngle,
          variant: 'liverecording',
          offset: null,
          startTime: this.parseStartTimeAsMillies(liveStream),
          format: p.type,
          streamId: liveStream.streamId,
          url: p.url
        } as Video)
    );
  }

  private parseStartTimeAsMillies(liveStream: Stream): number | undefined {
    if (typeof liveStream.startTime === 'number') {
      return liveStream.startTime;
    } else if (typeof liveStream.startTime === 'string') {
      const date = new Date(liveStream.startTime);
      return isNaN(date.getTime()) ? undefined : date.getTime();
    } else {
      return undefined;
    }
  }

  mapStreamToCameraAngle(camera: string) {
    switch (camera) {
      case 'behindgoal1':
        return CameraAngle.BEHIND_GOAL_1;
      case 'behindgoal2':
        return CameraAngle.BEHIND_GOAL_2;
      case 'corner1':
        return CameraAngle.CORNER_1;
      case 'corner2':
        return CameraAngle.CORNER_2;
      case 'overgoal1':
        return CameraAngle.OVER_GOAL_1;
      case 'overgoal2':
        return CameraAngle.OVER_GOAL_2;
      case 'closeup':
        return CameraAngle.CLOSE_UP;
      case 'fallback':
        return CameraAngle.FALLBACK;
      case 'tvfeed':
        return CameraAngle.TV_FEED;
      case 'gameclock':
        return CameraAngle.GAME_CLOCK;
      case 'multiview':
        return CameraAngle.MULTIVIEW;
      case 'panoramic':
        return CameraAngle.PANORAMIC;
      case 'main':
      default:
        return CameraAngle.MAIN;
    }
  }
}
